/**
 * API Document Template Provider
 *
 * Implements DocumentTemplateDataProvider using the BFF API with axios.
 * Used for production and staging environments.
 */

import type { DocumentTemplate } from "../../../types";
import axiosClient from "../../api/axiosClient";
import type { DocumentTemplateDataProvider } from "../types";

/**
 * DBDocumentTemplate format (snake_case from BFF) to DocumentTemplate format (camelCase for UI)
 */
interface DBDocumentTemplate {
	id?: string;
	template_key: string;
	name: string;
	url: string;
	description?: string;
	created_at: string;
	updated_at: string;
	updated_by?: string | null;
}

/**
 * Transform DBDocumentTemplate (snake_case) to DocumentTemplate (camelCase)
 */
function transformDBDocumentTemplateToDocumentTemplate(
	dbTemplate: DBDocumentTemplate,
): DocumentTemplate {
	return {
		id: dbTemplate.id || dbTemplate.template_key,
		name: dbTemplate.name,
		description: dbTemplate.description || "",
		url: dbTemplate.url,
		checklistItemId: dbTemplate.template_key, // template_key maps to checklistItemId
		createdAt: dbTemplate.created_at,
		updatedAt: dbTemplate.updated_at,
	};
}

export const apiDocumentTemplateProvider: DocumentTemplateDataProvider = {
	async getAll(): Promise<DocumentTemplate[]> {
		const response = await axiosClient.get<DBDocumentTemplate[]>(
			"/api/documents/templates",
		);
		// Response interceptor already extracted .data, so response is array
		if (Array.isArray(response)) {
			return response.map(transformDBDocumentTemplateToDocumentTemplate);
		}
		return [];
	},

	async getById(id: string): Promise<DocumentTemplate | null> {
		try {
			const dbTemplate = (await axiosClient.get(
				`/api/documents/templates/${id}`,
			)) as unknown as DBDocumentTemplate;
			if (!dbTemplate) return null;
			return transformDBDocumentTemplateToDocumentTemplate(dbTemplate);
		} catch (error: unknown) {
			if (
				error &&
				typeof error === "object" &&
				"status" in error &&
				error.status === 404
			) {
				return null;
			}
			throw error;
		}
	},

	async getByChecklistId(
		checklistItemId: string,
	): Promise<DocumentTemplate | null> {
		try {
			const dbTemplate = (await axiosClient.get(
				`/api/documents/templates?checklist-item-id=${encodeURIComponent(checklistItemId)}`,
			)) as unknown as DBDocumentTemplate;
			if (!dbTemplate) return null;
			return transformDBDocumentTemplateToDocumentTemplate(dbTemplate);
		} catch (error: unknown) {
			if (
				error &&
				typeof error === "object" &&
				"status" in error &&
				error.status === 404
			) {
				return null;
			}
			throw error;
		}
	},

	async create(
		template: Omit<DocumentTemplate, "id" | "createdAt" | "updatedAt">,
	): Promise<DocumentTemplate> {
		// Transform DocumentTemplate to DBDocumentTemplate format for request
		const requestBody: Partial<DBDocumentTemplate> = {
			template_key: template.checklistItemId,
			name: template.name,
			url: template.url,
			description: template.description,
		};
		const dbTemplate = (await axiosClient.post(
			"/api/documents/templates",
			requestBody,
		)) as unknown as DBDocumentTemplate;
		return transformDBDocumentTemplateToDocumentTemplate(dbTemplate);
	},

	async update(
		id: string,
		updates: Partial<DocumentTemplate>,
	): Promise<DocumentTemplate> {
		// BFF only accepts name, url, and description (template_key cannot be changed)
		const requestBody: Partial<DBDocumentTemplate> = {};
		if (updates.name !== undefined) requestBody.name = updates.name;
		if (updates.url !== undefined) requestBody.url = updates.url;
		if (updates.description !== undefined)
			requestBody.description = updates.description;
		// Note: checklistItemId (template_key) cannot be updated per BFF implementation

		const dbTemplate = (await axiosClient.patch(
			`/api/documents/templates/${id}`,
			requestBody,
		)) as unknown as DBDocumentTemplate;
		return transformDBDocumentTemplateToDocumentTemplate(dbTemplate);
	},

	async delete(id: string): Promise<void> {
		await axiosClient.delete(`/api/documents/templates/${id}`);
	},
};
